import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import apiService from '../services/apiService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isCheckingToken, setIsCheckingToken] = useState(false);

  // Global logout function that can be called from anywhere
  const logout = useCallback(() => {
    console.log('Logging out user due to token expiration or authentication failure');
    setUser(null);
    localStorage.removeItem('fciveria_admin_user');
    
    // Redirect to login page if we're in admin area
    if (window.location.pathname.startsWith('/admin')) {
      window.location.href = '/admin/login';
    }
  }, []);

  // Check token validity and auto-logout if expired
  const checkTokenValidity = useCallback(async () => {
    if (!user || !user.token || isCheckingToken) {
      console.log('Skipping token check:', { hasUser: !!user, hasToken: !!user?.token, isChecking: isCheckingToken });
      return;
    }
    
    console.log('Starting token verification...');
    setIsCheckingToken(true);
    try {
      const isValid = await apiService.verifyToken();
      console.log('Token verification result:', isValid);
      if (!isValid) {
        console.log('Token is invalid, logging out...');
        logout();
      }
    } catch (error) {
      console.log('Token verification failed, logging out...', error);
      logout();
    } finally {
      setIsCheckingToken(false);
    }
  }, [user?.token, logout, isCheckingToken]); // Only depend on token, not entire user object

  useEffect(() => {
    // Check for stored authentication
    const storedUser = localStorage.getItem('fciveria_admin_user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        
        // Only check token validity if we're in admin area
        if (window.location.pathname.startsWith('/admin')) {
          setTimeout(() => {
            checkTokenValidity();
          }, 2000); // Increased delay to 2 seconds
        }
      } catch (error) {
        console.error('Invalid stored user data:', error);
        localStorage.removeItem('fciveria_admin_user');
      }
    }
    setLoading(false);
  }, []); // Remove checkTokenValidity dependency to prevent loop

  // Set up periodic token verification (every 10 minutes)
  useEffect(() => {
    if (!user) return;
    
    const interval = setInterval(() => {
      checkTokenValidity();
    }, 10 * 60 * 1000); // 10 minutes
    
    return () => clearInterval(interval);
  }, [user?.token, checkTokenValidity]); // Only depend on token

  // Listen for global logout events (e.g., from API service on 401 errors)
  useEffect(() => {
    const handleGlobalLogout = (event) => {
      console.log('Global logout event received:', event.detail);
      logout();
    };

    window.addEventListener('auth:logout', handleGlobalLogout);
    
    return () => {
      window.removeEventListener('auth:logout', handleGlobalLogout);
    };
  }, [logout]);

  const login = async (identifier, password) => {
    try {
      const res = await fetch(`${apiService.baseUrl.replace(/\/$/, '')}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: identifier, email: identifier, password })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return { success: false, error: err.error || 'Login failed' };
      }
      const data = await res.json();
      const userData = { ...data.user, token: data.token };
      setUser(userData);
      localStorage.setItem('fciveria_admin_user', JSON.stringify(userData));
      return { success: true };
    } catch (e) {
      return { success: false, error: 'Network error' };
    }
  };

  const value = {
    user,
    login,
    logout,
    loading,
    checkTokenValidity
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
